import { useCallback, useMemo } from 'react';

import { useSaveCareerProfileFormMutation } from 'core';
import { ICareerProfileFormValues } from 'organisms';
import { ICareerProfileData, mapProfileNode, transformProfileToInput } from './transform';

type SaveCareerProfile = [
  (values: ICareerProfileFormValues, photo?: File | null, clearPhoto?: boolean) => Promise<unknown>,
  {
    loading: boolean;
    success: boolean;
    error?: string | null;
    /** The freshly saved profile (fragment) mapped back to the view model. */
    savedProfile: ICareerProfileData | null;
  },
];

/**
 * Persists the whole profile (spec 0018, increment B).
 *
 * The mutation returns the FULL profile, so there is NO `refetchQueries`: the
 * caller resets the form baseline from `savedProfile` instead of a refetch that
 * would reinitialize Formik and wipe edits typed during the round-trip. The
 * returned CareerProfileType (normalised by id) also refreshes the
 * `myCareerProfile` cache, so the completeness bar / locales stay current.
 */
export const useSaveCareerProfile = (): SaveCareerProfile => {
  const [mutate, { data, loading }] = useSaveCareerProfileFormMutation();

  const save = useCallback(
    (values: ICareerProfileFormValues, photo?: File | null, clearPhoto?: boolean) =>
      mutate({
        variables: {
          input: transformProfileToInput(values),
          // A File switches Apollo to multipart upload; `clearPhoto` removes an
          // existing avatar. Sending neither leaves the current photo untouched.
          photo: photo ?? undefined,
          clearPhoto: clearPhoto ? true : undefined,
        },
      }),
    [mutate],
  );

  const savedProfile = useMemo<ICareerProfileData | null>(() => {
    const node = data?.saveCareerProfile?.profile;
    return node ? mapProfileNode(node) : null;
  }, [data]);

  return [
    save,
    {
      loading,
      success: Boolean(data?.saveCareerProfile?.success),
      error: data?.saveCareerProfile?.customErrors?.[0]?.message,
      savedProfile,
    },
  ];
};
